import webApiClient from "./client";

/**
 * Payment API Client
 *
 * Handles Stripe payment operations:
 * - Payment intent creation (authenticated and guest)
 * - Stripe Connect status and financial data
 * - Balance, transactions, payouts, earnings
 */

export const paymentApi = {
    // ====================
    // Tips (mascot demo instructors)
    // ====================

    /**
     * Start a tip to Dance Localé through a mascot (demo) instructor. Signed-in only.
     * @param {string} instructorId - the mascot org id
     * @param {number} amount - integer cents (min 100)
     * @returns {Promise<{transactionId, token, checkoutUrl, amount, currency}>}
     * @throws {Error} with the backend message on failure
     */
    createTip: async (instructorId, amount) => {
        const response = await webApiClient.post("/api/v2/payment/tip", { instructorId, amount });
        if (!response.ok || !response.data?.tip) {
            throw new Error(response.data?.message || "Failed to start your tip");
        }
        return response.data.tip;
    },

    /**
     * Resolve a tip pay link (public). clientSecret is present only while payable.
     * @param {string} token - the /tip/:token pay-link token
     * @returns {Promise<{status: 'pending'|'succeeded', amount, currency, clientSecret?}>}
     */
    getTip: async token => {
        const response = await webApiClient.get(`/api/v2/payment/tip/${token}`);
        if (!response.ok || !response.data?.tip) {
            throw new Error(response.data?.message || "This tip link is not valid");
        }
        return response.data.tip;
    },

    // ====================
    // Payment Intent Creation
    // ====================

    /**
     * Create a Stripe payment intent for an authenticated user
     * Used for ticket purchases and booking payments
     * @param {object} payload - Payment details
     * @param {string} payload.sourceType - 'ticket' | 'booking'
     * @param {string} payload.sourceId - Ticket or booking ID
     * @param {string} payload.organisationId - Organisation receiving payment
     * @param {number} payload.amount - Amount in smallest currency unit (e.g. cents)
     * @param {string} payload.currency - ISO 4217 currency code, uppercase (e.g. 'AUD')
     * @returns {Promise<object>} { paymentIntent: { clientSecret }, transactionId }
     * @throws {Error} If payment intent creation fails
     */
    createPaymentIntent: async payload => {
        const response = await webApiClient.post("/api/v2/payment/create", payload);

        if (!response.ok || !response.data) {
            const error = new Error(response.data?.message || "Failed to create payment intent");
            error.status = response.status;
            error.errorCode = response.data?.errorCode;
            throw error;
        }

        return response.data;
    },

    /**
     * Create a Stripe payment intent for a guest (unauthenticated) user
     * Used for guest ticket purchases and guest booking payments
     * @param {object} payload - Payment details
     * @param {string} payload.sourceType - 'ticket' | 'booking'
     * @param {string} payload.sourceId - Ticket or booking ID
     * @param {string} payload.organisationId - Organisation receiving payment
     * @param {string} payload.guestEmail - Guest's email address
     * @param {number} payload.amount - Amount in smallest currency unit
     * @param {string} payload.currency - ISO 4217 currency code
     * @returns {Promise<object>} { paymentIntent: { clientSecret }, transactionId }
     * @throws {Error} If payment intent creation fails
     */
    /**
     * Price a ticket selection before checkout (public, read-only): the same validation
     * and fee calculation the payment session uses, so the page can show the Service fee
     * and the full total before Purchase. Returns { basePrice, platformFee, processingFee,
     * totalPrice, currency } in cents.
     */
    quoteTicketCharge: async ({ sourceId, ticketSelections }) => {
        const response = await webApiClient.post("/api/v2/payment/quote", {
            sourceType: "ticket",
            sourceId,
            ticketSelections,
        });

        if (!response.ok || !response.data?.fees) {
            const error = new Error(response.data?.message || "Failed to price this selection");
            error.status = response.status;
            error.errorCode = response.data?.errorCode || response.data?.code;
            throw error;
        }

        return response.data.fees;
    },

    createGuestPaymentIntent: async payload => {
        const response = await webApiClient.post("/api/v2/payment/create-guest", payload);

        if (!response.ok || !response.data) {
            const error = new Error(response.data?.message || "Failed to create guest payment intent");
            error.status = response.status;
            error.errorCode = response.data?.errorCode;
            throw error;
        }

        return response.data;
    },

    // ====================
    // Stripe Connect
    // ====================

    /**
     * Get Stripe Connect onboarding and account status for an organisation
     * @param {string} organisationId - Organisation ID
     * @returns {Promise<object>} Connect status including chargesEnabled, payoutsEnabled, etc.
     * @throws {Error} If fetch fails
     */
    getConnectStatus: async organisationId => {
        const response = await webApiClient.get(`/api/v2/payment/connect/status/${organisationId}`);

        if (!response.ok || !response.data) {
            throw new Error(response.data?.message || "Failed to fetch Connect status");
        }

        return response.data;
    },

    // ====================
    // Financial Data
    // ====================

    /**
     * Get Stripe account balance for an organisation
     * @param {string} organisationId - Organisation ID
     * @returns {Promise<object>} Balance object including available and pending amounts
     * @throws {Error} If fetch fails
     */
    getBalance: async organisationId => {
        const response = await webApiClient.get(`/api/v2/payment/balance/${organisationId}`);

        if (!response.ok || !response.data) {
            throw new Error(response.data?.message || "Failed to fetch balance");
        }

        return response.data;
    },

    /**
     * Get transaction history for an organisation
     * @param {string} organisationId - Organisation ID
     * @param {number} [limit=100] - Maximum number of transactions to return
     * @returns {Promise<Array>} Array of transaction objects
     * @throws {Error} If fetch fails
     */
    getTransactions: async (organisationId, limit = 100) => {
        const response = await webApiClient.get(
            `/api/v2/payment/transactions?organisationId=${organisationId}&limit=${limit}`
        );

        if (!response.ok || !response.data || !response.data.transactions) {
            throw new Error(response.data?.message || "Failed to fetch transactions");
        }

        return response.data.transactions;
    },

    /**
     * Get payout history for an organisation
     * @param {string} organisationId - Organisation ID
     * @param {number} [limit=50] - Maximum number of payouts to return
     * @returns {Promise<Array>} Array of payout objects
     * @throws {Error} If fetch fails
     */
    getPayouts: async (organisationId, limit = 50) => {
        const response = await webApiClient.get(
            `/api/v2/payment/payouts/${organisationId}?limit=${limit}`
        );

        if (!response.ok || !response.data || !response.data.payouts) {
            throw new Error(response.data?.message || "Failed to fetch payouts");
        }

        return response.data.payouts;
    },

    /**
     * Get earnings summary for an organisation
     * @param {string} organisationId - Organisation ID
     * @returns {Promise<object>} Earnings summary with totals and breakdowns
     * @throws {Error} If fetch fails
     */
    getEarnings: async organisationId => {
        const response = await webApiClient.get(`/api/v2/payment/earnings/${organisationId}`);

        if (!response.ok || !response.data) {
            throw new Error(response.data?.message || "Failed to fetch earnings");
        }

        return response.data;
    },

    /**
     * Fetch public org branding for payment page display (no auth required).
     * Gracefully returns empty branding on failure so payment pages still render.
     * @param {string} orgId - Organisation ID
     * @returns {Promise<{orgName: string, branding: object}>}
     */
    getOrgBrandingPublic: async (orgId) => {
        try {
            const response = await webApiClient.get(`/api/v2/org/${orgId}/branding-public`);
            if (!response.ok) return { orgName: '', branding: {}, privateLessonPolicy: null };
            return {
                orgName: response.data.orgName || '',
                branding: response.data.branding || {},
                privateLessonPolicy: response.data.privateLessonPolicy || null,
            };
        } catch (_) {
            return { orgName: '', branding: {}, privateLessonPolicy: null };
        }
    },
};
